import { useState } from "react";
import LmsFormGenerator, { BCFormController, IFormGeneratorFieldOptions } from "../ui/LmsFormGenerator";
import LmsTabPages from "../ui/LmsTabPages";
import LmsTable from "../ui/LmsTable";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { TABLES } from "../../constants/GlobalLabelsDictionaries";
import { useParseRouteActions, useFetchData, removeProp, appendParam } from "../../../helpers/GlobalHelper";
import { IDefaultPagesProps } from "../../interfaces/Lms";
import {
  LmsLoanDetailsTable,
  LmsLoanPropertyTable,
  LmsLoanCommentsTable,
  LmsLoanApplicationsEntitiesTable,
  LmsLoanConditionsTable
} from "../ui/LmsSharedTables";
import BCModal from "../../ui/BCModal";
import { UploadBudgetForm } from "../ui/LmsSharedForms";
import { isGuid } from "../../../Helpers";
import { AddContactForm } from "../Organizations/LmsOrganizationsContacts";
import { AddSPEBorrowerOrganization } from "../Organizations/LmsOrganizationsBorrowers";
import { loanPropertyUnitReconveyanceFields } from "../Servicing/LmsServicingUnits";
import { loanConditionFields } from "../Servicing/LmsServicingLoanConditions";
import LmsActionDropdown from "../ui/LmsActionDropdown";
import LmsActionInput from "../ui/LmsActionInput";
import { InsuranceParams, INSURANCE_BASE_PATH, INSURANCE_PATHS } from "../Servicing/LmsServicingInsurance";

export const LmsLoanApplicationConditionsTable = ({ suffix = "", title = "", source = "Loan", loanDetailsId = "" }) => {
  const loanDetailsParam = loanDetailsId ? `&loanDetailsId=${loanDetailsId}` : "";
  const conditionActionButtons = [
    { text: "Add", icon: GlobalConstants.Icons.ADD, path: `/loandetails/addcondition?conditionSource=${source}${loanDetailsParam}` },
    { text: "Edit", path: "/loandetails/editcondition" },
    "remove",
    "export",
    "save",
    "cancel"
  ];

  return <LmsTable
    actionButtons={conditionActionButtons}
    apiSuffix={suffix}
    title={title}
    onClickPath="loandetails/editcondition"
    columnLabelsDictionary={TABLES.CONDITIONS}
    myForm={loanConditionFields(new BCFormController())}
    saveSuffix="loandetails/conditions"
  />;
};

const AddGuarantorTable = ({ loanId = "" }) => {
  const actionButtons = [
    "export",
    { action: "addselected" , text: "Add Guarantor" }
  ];

  return <LmsTable
    actionButtons={actionButtons}
    apiSuffix={`loans/${loanId}/guarantors`}
    addSelectedSuffix={`loans/${loanId}/link-guarantors`}
  />;
};

const addMiniLoanTableFields = (myForm) => {
  myForm.addCenteredGroup([
    myForm.getBlankField({ width: "2" }),
    myForm.getGroupRadioField("Mini Loans in Servicing", { width: "2" }),
    myForm.getGroupRadioField("Mini Loans in Pipeline", { width: "2" }),
    myForm.getBlankField({ width: "1" })
  ], { radioGroupName: "submittedMiniLoansOption" });
  myForm.addCenteredGroup([
    myForm.getBlankField({ width: "2" }),
    myForm.getLabelField("Current", { width: "2", position: "top-center" }),
    myForm.getLabelField("After Mini Approval", { width: "2", position: "top-center" }),
  ],{ groupClassName: "mini-loans-form-table-row", isCompact: true });
  myForm.addCenteredGroup([
    myForm.getLabelField("Remaining Dev Capacity", { width: "2", position: "top-right", wrapLabel: true }),
    myForm.getCurrencyField("", { name: "submittedCurrentBucketCapacity", width: "2", disabled: true, isCompact: true }),
    myForm.getCurrencyField("", {
      name: "submittedBucketCapacityWithMiniLoansInPipeline",
      width: "2",
      disabled: true,
      isHidden: { submittedMiniLoansOption: "Mini Loans in Servicing" },
      isCompact: true
    }),
    myForm.getCurrencyField("", {
      name: "submittedBucketCapacityWithThisMiniLoan",
      width: "2",
      disabled: true,
      isHidden: { submittedMiniLoansOption: "Mini Loans in Pipeline" },
      isCompact: true
    }),
  ], { groupClassName: "mini-loans-form-table-row", isCompact: true });
  myForm.addCenteredGroup([
    myForm.getLabelField("Dev Temporary Reserve Capacity", { width: "2", position: "top-right", wrapLabel: true }),
    myForm.getCurrencyField("", { name: "submittedCurrentTrc", width: "2", disabled: true, isCompact: true }),
    myForm.getCurrencyField("", {
      name: "submittedTrcwithMiniLoansInPipeline",
      width: "2",
      disabled: true,
      isHidden: { submittedMiniLoansOption: "Mini Loans in Servicing" },
      isCompact: true
    }),
    myForm.getCurrencyField("", {
      name: "submittedTrcwithThisMiniLoan",
      width: "2",
      disabled: true,
      isHidden: { submittedMiniLoansOption: "Mini Loans in Pipeline" },
      isCompact: true
    }),
  ], { groupClassName: "mini-loans-form-table-row", isCompact: true });
  myForm.addDividerField({ dividerIsHidden: true });
};

const addApplicationFormFields = (myForm, isEdit = false, loanId = "") => {
  const onSubmitSPESuccess = async ({ id }, options) => {
    if (id && options?.handleFormChange) {
      options?.handleFormChange(undefined, { name: options?.name, value: id }, true);
    }
  };

  const speOptions = {
    dependencyField: "borrowerOrganizationId",
    isHiddenFunc: (options) => !!options?.inputs?.borrowerOrganizationId
  } as IFormGeneratorFieldOptions;

  const speAddOptions = {
    ...speOptions,
    actionLabel: "Add New SPE",
    modalContent: (setIsAddFormModalOpen, options) => {
      return <AddSPEBorrowerOrganization
        orgId={options?.useFormInstance?.inputs?.borrowerOrganizationId}
        onSubmitSuccess={(responseData) => onSubmitSPESuccess(responseData, options)}
        handleClose={() => setIsAddFormModalOpen(false)}
        saveAndCloseOnly
      />;
    },
    modalSize: "fullscreen",
    actionIcon: GlobalConstants.Icons.CARET_UP,
    isHiddenFunc: (options) => !options?.inputs?.borrowerOrganizationId
  } as IFormGeneratorFieldOptions;


 if (myForm?.metaData?.isMiniLoan) {
   addMiniLoanTableFields(myForm);
 }

  myForm.addGroupFields([
    myForm.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "borrowerOrganizationId", required: true }),
    myForm.getDropdownField("Special Purpose Entity", "orgs/list-spes", speOptions),
    myForm.getDropdownField("Special Purpose Entity", "orgs/list-spes", speAddOptions),
    myForm.getInputField("Project Name", { name: "name", required: true }),
    myForm.getCheckboxField("Partnered Loan"),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getDropdownField("Loan Program Requested", "metadata/list-loan-programs", { name: "loanProgramId", required: true }),
    myForm.getDropdownField("Transaction Type", "metadata/list-loan-sub-types", { name: "loanSubTypeId", required: true }),
    myForm.getDropdownField("How did you hear about us?", "metadata/list-referral-sources", { name: "referralSourceId", width: "4" }),
    myForm.getInputField("Cumulative Project Experience", { width: "4", required: true }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getInputField("Name of Referral", { name: "referralDetails", width: "8" }),
    myForm.getInputField("Property Address", { name: "street", width: "8", required: true }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getZipCodeField("Zip Code", { width: "4" }),
    myForm.getStateField("State", { width: "4" }),
    myForm.getCityField("City", { width: "4" }),
    myForm.getCountyField("County", { width: "4" }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getDropdownField("Property Type", "metadata/list-property-types", { width: "4", required: true }),
    myForm.getInputField("Number Of Units", { width: "4", required: true }),
    myForm.getNumberField("Loan Term Requested (Months)", { name: "termOfLoan", width: "4", required: true }),
    myForm.getInputField("Estimated Closing Date", { field: "calendar", width: "4", required: true }),
  ], { widths: "4" });
  myForm.addGroupFields([
    myForm.getCurrencyField("Loan Amount Requested", { name: "estimatedConstructionBudget", width: "4", required: true }),
    myForm.getCurrencyField("Additional Liens ($)", { name: "additionalMortgage", width: "4" }),
    myForm.getDropdownField("Name of Builder", "metadata/list-builder-organizations", { name: "builderOrganizationId", width: "4" }),
    myForm.getDropdownField("Name of Loan Officer", "metadata/list-preferrred-loan-officers", { name: "closingLoanOfficerOrganizationContactId", width: "4" }),
  ], { widths: "4" });

  const contactOptions = {
    name: "loanContactOrganizationContactId",
    required: true,
    dependencyField: "borrowerOrganizationId",
    width: 4
  } as IFormGeneratorFieldOptions;

  myForm.addGroupFields([
    myForm.getDropdownField("Loan Contact", "orgs/list-loan-org-contacts", contactOptions),
  ], { isHiddenFunc: (options) => !!options?.inputs?.borrowerOrganizationId });

  const contactAddActionOptions = {
    ...contactOptions,
    actionLabel: "Add New Contact",
    modalContent: (setIsAddFormModalOpen, options) => <AddContactForm orgId={options?.useFormInstance?.inputs?.borrowerOrganizationId} setIsModalOpen={setIsAddFormModalOpen as any} dropdownOptions={options as any} />,
    modalSize: "fullscreen",
    actionIcon: GlobalConstants.Icons.CARET_UP,
    width: 5
  } as IFormGeneratorFieldOptions;

  myForm.addGroupFields([
    myForm.getDropdownField("Loan Contact", "orgs/list-loan-org-contacts", contactAddActionOptions),
  ], { isHiddenFunc: (options) => !options?.inputs?.borrowerOrganizationId, widths: 2 });

  if (isEdit) {
    myForm.addGroupFields([myForm.getButtonField("Add Guarantor", undefined, {
      modalContent: () => <AddGuarantorTable loanId={loanId} />,
      fullWidth: true,
      width: 2,
      buttonIcon: GlobalConstants.Icons.CARET_UP
    })], { widths: "2" });
  }

  return myForm;
};

const AddApplicationForm = () => {
  const myForm = addApplicationFormFields(new BCFormController({}, {
    submitSuffix: "loans"
  }));
  const formActions = ["save-continue", "close"];
  return <LmsFormGenerator myForm={myForm} actions={formActions} />;
};

const EditApplicationForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`loans/${id}`);
  const myForm = addApplicationFormFields(new BCFormController(data, {
    submitSuffix: "loans",
    metaData: { isMiniLoan: data?.masterLoanId && data?.masterLoanId !== id }
  }), true, id);
  const actions = ["save", "save-close", "close"];
  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={actions} />;
};

export const AddMiniLoanApplicationForm = ({ handleClose = undefined as any, minLoanTempId }) => {
  const [{ data, isLoading, error }] = useFetchData(minLoanTempId ? `loans/mini-loan-temp-initial-data/${minLoanTempId}` : "");
  const myForm = new BCFormController(removeProp(data, ["id", "estimatedClosingDate"]), { submitSuffix: "loans" });
  myForm.addCenteredGroup([
    myForm.getDisplayField("Revolver Type", { name: "revolverTypeOption" }),
  ]);

  addMiniLoanTableFields(myForm);
  addApplicationFormFields(myForm);
  const formActions = ["save-continue", "close"];
  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={formActions}
      isLoading={isLoading}
      error={error}
      handleClose={handleClose}
      displayMessageLocally
    />
  );
};

const UploadDataModal = ({ setIsModalOpen }) => {
  const [isContinue, setIsContinue] = useState(false);
  const myForm = new BCFormController({}, {
    submitSuffix: "document/loan-temp-property-unit/upload",
    onSaveContinue: () => setIsContinue(true),
    payloadDataType: "FormData",
    blankWidth: 4
  });
  myForm.addCenteredGroup([myForm.getFileField("Attachment", { name: "assetFile", required: true })]);

  const formActions = ["save-continue", "close"];

  return <>
    {!isContinue && <LmsFormGenerator myForm={myForm} actions={formActions} displayMessageLocally handleClose={() => setIsModalOpen(false)} />}
    {isContinue && <div>Edit</div>} {/* TODO: Add Edit Form */}
  </>;
};

const AddPropertyTempApplicationForm = ( ) => {
  const myForm = addApplicationFormFields(new BCFormController({}, {
    submitSuffix: "loans",
    metaData: { isMiniLoan: true }
  }));

  const formActions = [{ action: "save-continue", text: "Next" }, "close"];
  return <LmsFormGenerator myForm={myForm} actions={formActions} />;
};

const LoanTempPropertyUnitTable = ({ id }) => {
  const editPath = "loanapplications/editloantempproperty";
  const [miniLoanValue, setMiniLoanValue] = useState("");
  const [isLoanModalOpen, setIsLoanModalOpen] = useState(false);
  const [checkBoxValues, setCheckBoxValues] = useState({ newLoan: true } as any);
  const handleCheckBoxChange = (value, name) => {
    setCheckBoxValues({ [name]: value });

    if (name === "newLoan" && value) {
      setMiniLoanValue("");
    }
  };

  const handleDropdownChange = (_, { value }) => {
    setMiniLoanValue(value);
  };

  return (
    <>
      <LmsTable
        apiSuffix={`loandetails/temp-properties/${id}`}
        deleteSuffix="loandetails/temp-properties"
        actionButtons={[
          { action: "add", path: `loanapplications/addloantempproperty/${id}` },
          { action: "edit", path: editPath },
          "remove",
          "export",
          "save",
          "cancel",
          {
            text: "Export To Update",
            action: GlobalConstants.TableDefaultActions.DOWNLOAD_SELECTED,
            method: "POST",
            payloadContentType: "JSON",
            data: (selectedIds) => ({ loanPropertyIds: selectedIds }),
            downloadSuffix: "document/loan-temp-property-unit/export",
            fileName: "Property Unit.xlsx",
            downloadMany: true
          },
          {
            text: "Upload Data",
            modalContent: (setIsModalOpen) => <UploadDataModal setIsModalOpen={setIsModalOpen} />,
            action: GlobalConstants.TableDefaultActions.MODAL_BUTTON,
            isDisabled: () => false
          },
          <LmsActionInput handleChange={handleCheckBoxChange} name="newLoan" label="New Loan" value={!!checkBoxValues?.newLoan} type="checkbox" />,
          <LmsActionInput handleChange={handleCheckBoxChange} name="existingLoan" label="Existing Loan" value={!!checkBoxValues?.existingLoan} type="checkbox" />,
          <LmsActionDropdown optionsSuffix={`loandetails/list-mini-loans-by-tempId/${id}`} handleChange={handleDropdownChange} value={miniLoanValue} disabled={!checkBoxValues?.existingLoan} />,
          {
            text: "Next",
            action: GlobalConstants.TableDefaultActions.SUBMIT_SELECTED,
            method: "POST",
            submitSuffix: appendParam(`loandetails/validate-loan-temp/${id}`, "loanId",  miniLoanValue),
            onSuccessFunc: () => {
              if (!miniLoanValue) {
                setIsLoanModalOpen(true);
              }
            },
            isDisabled: () => false,
            data: () => null,
          }
        ]}
        columnLabelsDictionary={propertyUnitTableColumnsDictionary}
        saveSuffix="loandetails/temp-properties"
        myForm={loanPropertyUnitReconveyanceFields(new BCFormController())}
        onClickPath={editPath}
      />
      {isLoanModalOpen && <BCModal
        isModalOpen
        size="fullscreen"
        closeIcon
        onClose={() => setIsLoanModalOpen(false)}
        content={<AddPropertyTempApplicationForm />}
      />}
    </>
  );
};

const ViewApplicationForm = ({ id }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(`loans/${id}`);
  const myForm = addApplicationFormFields(new BCFormController(data, { initIsDisabled: true }));
  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={["close"]}/>;
};

const LoanApplicationsTable = () => {
  return <LmsTable
    actionButtons={["add", "edit", "copy", "remove", "export"]}
    apiSuffix="loans/applications"
    deleteSuffix="loans"
    sortable={["CreatedBy", "CityId", "Borrower"]}
    currencyFields={["estimatedConstructionBudget", "wireAmount"]}
    onClickPath="loanapplications/view"
    useApiFiltering
    columnLabelsDictionary={{
      name: "Project Name",
      loanSubType: "Transaction Type",
      borrowerOrganization: "Borrower",
      builder: "Name of Builder",
      termOfLoan: "Loan Term Requested (Months)",
      estimatedConstructionBudget: "Loan Amount Requested",
      applicationNumber: "Application #",
      loanNumber: "Loan #",
      jurisdiction: "Permit Jurisdiction",
      street: "Property Address",
      zip: "Zip Code",
      loanProgram: "Loan Program Requested",
      brms: "BRMs",
      initialCos: "Initial COs",
      reviewCos: "Review COs",
      trctriggered: "TRC Triggered",
      spreadRate: "Margin",
      rateFloor: "Floor",
      rateCeiling: "Ceiling",
      batierRate: "BA Tier Rate",
      additionalMortgage: "Additional Liens"
    }}
  />;
};

export const AddTPValuationProvider = ({ setIsModalOpen, options }) => {
  const onSubmitSuccess = (responseData) => {
    const resId = responseData && isGuid(responseData) ? responseData : responseData?.id;

    if (resId) {
      options?.handleFormChange(null, { value: resId, name: options?.name }, true);
    }
  };

  const myForm = new BCFormController({}, { submitSuffix: "loanproperty/tpvaluation-provider", onSubmitSuccess });
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getInputField("Name", { width: 8 }),
    myForm.getCheckboxField("Is Active", { width: 4, value: true }),
    myForm.getBlankField(),
  ], { widths: 4 });
  myForm.addGroupFields([
    myForm.getBlankField(),
    myForm.getTextareaField("Description", { width: 12 }),
    myForm.getBlankField(),
  ], { widths: 4 });
  const formActions = ["save", "save-close", "close"];

  return <LmsFormGenerator myForm={myForm} actions={formActions} handleClose={() => setIsModalOpen(false)} displayMessageLocally />;
};

const addPropertyFields = (myForm, loanPropertyId = "") => {
  myForm.addGroupFields([
    myForm.getDropdownField("Borrower", "metadata/list-borrower-organizations", { name: "borrowerOrganizationId", disabled: true }),
    myForm.getDropdownField("Special Purpose Entity", "orgs/list-special-purpose-entities", { dependencyField: "borrowerOrganizationId", disabled: true }),
    myForm.getInputField("Application #", { name: "applicationNumberDisplay", disabled: true }),
    myForm.getInputField("Loan #", { name: "loanNumberDisplay", disabled: true }),
    myForm.getDropdownField("Current Property Type", "metadata/list-property-types", { name: "propertyTypeId", required: true }),
  ]);
  myForm.addGroupFields([
    myForm.getDropdownField("Flood Zone", "metadata/list-flood-zone"),
    myForm.getDropdownField("Future Property Type", "metadata/list-property-types", { name: "newStructurePropertyTypeId", required: true }),
    myForm.getDropdownField("Loan Program Requested", "metadata/list-loan-programs?includeRevolver=true", { name: "loanProgramId", disabled: true }),
    myForm.getDropdownField("Transaction Type", "metadata/list-loan-sub-types", { name: "loanSubTypeId", disabled: true }),
    myForm.getDropdownField("Permit Issued", "metadata/list-permit-status", { name: "permitStatusId" }),
  ]);
  myForm.addGroupFields([
    myForm.getTextareaField("Parcel Number"),
    myForm.getInputField("Lot Number"),
    myForm.getNumberField("Number Of Units", { required: true }),
    myForm.getInputField("Plan Number"),
    myForm.getCheckboxField("Is this property additional collateral?", { name: "collateralProperty" })
  ]);
  myForm.addGroupFields([
    myForm.getCheckboxField("Additional Collateral DOT Recorded", { name: "additionalCollateralDotrecorded" }),
    myForm.getInputField("Additional Collateral DOT Number", { name: "additionalCollateralDotnumber" }),
    myForm.getBlankField(),
    myForm.getBlankField(),
    myForm.getBlankField(),
  ], { isHidden: { collateralProperty: true }, isHiddenOperation: "notequal" });
  myForm.addGroupFields([
    myForm.getInputField("Property Address", { required: true, name: "street" }),
    myForm.getZipCodeField("Zip", { width: "4" }),
    myForm.getStateField("State", { width: "4" }),
    myForm.getCityField("City", { width: "4" }),
    myForm.getCountyField("County", { width: "4", required: true }),
  ]);
  myForm.addGroupFields([
    myForm.getGroupRadioField("Land To Purchase", { name: "LandToPurchase" }),
    myForm.getGroupRadioField("Land Owned", { name: "LandOwned" }),
  ], { radioGroupName: "landOption", widths: "8", isHidden: { collateralProperty: true } });
  myForm.addGroupFields([
    myForm.getGroupRadioField("Land Owned", { name: "LandOwned" }),
  ], { radioGroupName: "landOption", widths: "8", isHidden: { collateralProperty: false }, isHiddenOperation: "isFalsy" });
  myForm.addGroupFields([
    myForm.getCurrencyField("Purchase Price", { name: "purchasedPrice" }),
    myForm.getCurrencyField("Land Financed Amount - Purchase", { name: "downPayment" }),
    myForm.getBlankField(),
    myForm.getBlankField(),
    myForm.getBlankField(),
  ], { isHidden: { landOption: "LandToPurchase" }, isHiddenOperation: "notequal" });
  myForm.addGroupFields([
    myForm.getCurrencyField("Refinance Price", { name: "lotValue" }),
    myForm.getCurrencyField("Land Financed Amount - Refinance", { name: "mortgagePayout" }),
    myForm.getCurrencyField("Original Purchase Price"),
    myForm.getBlankField(),
    myForm.getBlankField(),
  ], { isHidden: { landOption: "LandOwned" }, isHiddenOperation: "notequal" });
  myForm.addGroupFields([
    myForm.getCurrencyField("Cost Paid to Date", { name: "outOfPocket", required: true }),
    myForm.getCurrencyField("Subordinate Financing", { required: true, name: "subordinateFinancingAmount" }),
    myForm.getTextareaField("Description"),
    myForm.getTextareaField("Full Legal Description", { name: "legalDescription" }),
    myForm.getTextareaField("Abbreviated Legal Description", { name: "legalDescriptionAbbreviated" }),
  ]);
  myForm.addGroupFields([
    myForm.getCalendarField("Certificate Of Occupancy Date", { name: "coodate" }),
    myForm.getCalendarField("3rd Party Valuation Date", { name: "tpvaluationDate" }),
    myForm.getCurrencyField("As Repaired Value", { disabled: true, isHidden: { collateralProperty: true }, keepHiddenPosition: true }),
    myForm.getCurrencyField("Underwritten Value", { disabled: true, isHidden: { collateralProperty: true }, keepHiddenPosition: true }),
    myForm.getBlankField(),
  ]);

  const thirdPartyProviderOptions: IFormGeneratorFieldOptions = {
    name: "tpvaluationProviderId",
    actionLabel: "Add TP Valuation Provider",
    modalContent: (setIsAddFormModalOpen, options) => <AddTPValuationProvider setIsModalOpen={setIsAddFormModalOpen} options={options}/>,
    actionIcon: GlobalConstants.Icons.CARET_UP,
    width: 6
  };

  myForm.addGroupFields([
    myForm.getDropdownField("3rd Party Valuation Provider", "metadata/list-tpvaluation-provider", thirdPartyProviderOptions),
  ], { widths: 2 });

  myForm.addGroupFields([
    myForm.getInputField("Permit Issued Timing"),
    myForm.getDropdownField("Payoff Strategy", "metadata/list-payoff-strategy"),
    myForm.getTextareaField("Payoff Strategy Description"),
    myForm.getInputField("Zoning"),
    myForm.getDropdownField("% of Work Complete", "metadata/list-work-stage"),
  ]);

  const readyForWorkCheckbox = myForm.getCheckboxField("Ready For Work Inspection", { name: "readyForNwcc", inline: true, width: 3 });
  const workGroup = myForm.metaData.isEdit ? [
    myForm.getButtonField("Work Inspection Company History", undefined, {
      modalContent: () => <WorkInspectionCompanyHistory loanPropertyId={loanPropertyId} />,
      width: 4,
      fullWidth: true
    }),
    readyForWorkCheckbox
  ] : [readyForWorkCheckbox];

  myForm.addGroupFields(workGroup, {
    widths: 1,
    isHidden: { collateralProperty: true }
  });

  myForm.addCenteredGroup([
    myForm.getWarningTextField("Changing Purchase Price will change LTC", { isCompact: true }),
  ], { blankWidth: "2", isCompact: true, isHiddenFunc: ({ inputs }) => !inputs?.purchasedPrice });
  myForm.addCenteredGroup([
    myForm.getWarningTextField("Changing Subordinate Financing will change LTC, CLTV", { isCompact: true }),
  ], {
    blankWidth: "2",
    isCompact: true,
    isHiddenFunc: ({ inputs }) => !inputs?.subordinateFinancingAmount
  });
  myForm.addCenteredGroup([
    myForm.getWarningTextField("Changing Cost Paid to Date will change LTC", { isCompact: true }),
  ], { blankWidth: "2", isCompact: true, isHiddenFunc: ({ inputs }) => !inputs?.outOfPocket });
  myForm.addCenteredGroup([
    myForm.getWarningTextField("Changing Refinance Price will change LTC", { isCompact: true }),
  ], { blankWidth: "2", isCompact: true, isHiddenFunc: ({ inputs }) => !inputs?.lotValue });

  myForm.addGroupFields([
    myForm.getNumberField("Months Before Start"),
    myForm.getNumberField("Build Time In Months"),
    myForm.getNumberField("Time Frame To Sell Or Refinance In Months"),
    myForm.getNumberField("Month Of First Draw"),
    myForm.getBlankField()
  ]);

  return myForm;
};

const handleOnPropertyFormChange = ({ updateInput, inputs }, lastUpdated) => {
  if (lastUpdated === "collateralProperty" && inputs?.collateralProperty) {
    updateInput("landOption", "LandOwned");
  }
};

const AddPropertyForm = ({ loanId = "" }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(loanId ? `loanproperty/initial-data/${loanId}` : "");
  const handleAlertMessage = ({ inputs }) => {
    if (data?.existsSubordinateLender === false && !inputs?.collateralProperty && inputs?.subordinateFinancingAmount > 0) {
      return "Please add the Subordinate lender to the entities table";
    }

    return "";
  };

  const myForm = addPropertyFields(new BCFormController(removeProp(data, "id"), { submitSuffix: "loandetails/loan-temp-property" }));
  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} onFormChanged={handleOnPropertyFormChange} submitAlertMessage={handleAlertMessage} />;
};

const AddPropertyTempForm = () => {
  const handleAlertMessage = ({ inputs }) => {
    if (!inputs?.collateralProperty && inputs?.subordinateFinancingAmount > 0) {
      return "Please add the Subordinate lender to the entities table";
    }

    return "";
  };

  const myForm = addPropertyFields(new BCFormController({}, { submitSuffix: "loandetails/loan-temp-property" }));
  return <LmsFormGenerator myForm={myForm} onFormChanged={handleOnPropertyFormChange} submitAlertMessage={handleAlertMessage} />;
};

const EditPropertyTempForm = ({ id }) => {
  const loanPropertySuffix = `loandetails/temp-property/${id}`;
  const [{ data, isLoading, error }] = useFetchData(loanPropertySuffix);
  const propertyLoanId = data?.loanId || "";
  const myForm = addPropertyFields(new BCFormController(data, {
    values: { loanId: propertyLoanId },
    metaData: { isEdit: true },
    submitSuffix: "loandetails/loan-temp-property",
    saveAndContinueSuffix: "loanapplications/editloantempproperty",
    deleteSuffix: "loandetails/loan-temp-property",
    clearSuffixesOnSubmit: [loanPropertySuffix]
  }), id);

  return <>
    <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} onFormChanged={handleOnPropertyFormChange} />
  </>;
};

const addWorkInspectionCompanyFields = (myForm, loanPropertyData) => {
  myForm.addCenteredGroup([myForm.getDropdownField("Loan Property", loanPropertyData ? `loandetails/list-loan-properties/${loanPropertyData?.loanId}` : "", { required: true, disabled: true })]);
  myForm.addCenteredGroup([myForm.getDisplayField("Current Work Inspection Company")]);
  myForm.addCenteredGroup([myForm.getCalendarField("Effective Date", { required: true })]);
  myForm.addCenteredGroup([myForm.getDropdownField("Work Inspection Company Organization", "inspections/list-inspection-companies", { required: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Description", { required: true })]);
  return myForm;
};

const AddWorkInspectionCompanyForm = ({ loanPropertyId, setIsAddInspectionOpen, workInspectionsSuffix, loanPropertyData }) => {
  const myForm = addWorkInspectionCompanyFields(new BCFormController({ loanPropertyId }, {
    submitSuffix: "loanproperty/work-inspection-company-history",
    clearSuffixesOnSubmit: [workInspectionsSuffix]
  }), loanPropertyData);

  const formActions = ["save-new", "save-close", "close"];

  return <LmsFormGenerator myForm={myForm} actions={formActions} handleClose={() => setIsAddInspectionOpen(false)}/>;
};

const EditWorkInspectionCompanyForm = ({ inspectionId, setIsEditInspectionOpen, workInspectionsSuffix, loanPropertyData }) => {
  const workInspectionSuffix = `loanproperty/work-inspection-company-history/${inspectionId}`;
  const [{ data = {}, isLoading, error }] = useFetchData(workInspectionSuffix);

  const myForm = addWorkInspectionCompanyFields(new BCFormController(data, {
    submitSuffix: "loanproperty/work-inspection-company-history",
    clearSuffixesOnSubmit: [workInspectionsSuffix, workInspectionSuffix],
    deleteSuffix: "loanproperty/work-inspection-company-history"
  }), loanPropertyData);

  return <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} handleClose={() => setIsEditInspectionOpen(false)}/>;
};

const WorkInspectionCompanyHistory = ({ loanPropertyId }) => {
  const workInspectionsSuffix = `loanproperty/${loanPropertyId}/work-inspection-company-histories`;
  const [isAddInspectionOpen, setIsAddInspectionOpen] = useState(false);
  const [isEditInspectionOpen, setIsEditInspectionOpen] = useState(false);
  const [inspectionId, setInspectionId] = useState(false);
  const actionButtons = [{ text: "Add", handleClick: () => setIsAddInspectionOpen(true) }, "remove", "export"];
  const [{ data: loanPropertyData }] = useFetchData(`loanproperty/${loanPropertyId}`);

  return <>
    <BCModal
      isModalOpen={isAddInspectionOpen}
      size="large"
      closeIcon
      onClose={() => setIsAddInspectionOpen(false)}
      content={<AddWorkInspectionCompanyForm
        loanPropertyId={loanPropertyId}
        setIsAddInspectionOpen={setIsAddInspectionOpen}
        workInspectionsSuffix={workInspectionsSuffix}
        loanPropertyData={loanPropertyData}
      />}
    />
    <BCModal
      isModalOpen={isEditInspectionOpen && inspectionId}
      size="large"
      closeIcon
      onClose={() => setIsEditInspectionOpen(false)}
      content={<EditWorkInspectionCompanyForm
        inspectionId={inspectionId}
        setIsEditInspectionOpen={setIsEditInspectionOpen}
        workInspectionsSuffix={workInspectionsSuffix}
        loanPropertyData={loanPropertyData}
      />}
    />
    {!isAddInspectionOpen && !isEditInspectionOpen && <LmsTable
      title="Work Inspection Company History"
      apiSuffix={workInspectionsSuffix}
      deleteSuffix="loanproperty/work-inspection-company-history"
      actionButtons={actionButtons}
      handleClick={(options) => {
        setInspectionId(options.id);
        setIsEditInspectionOpen(true);
      }}
    />}
  </>;
};

const PropertyInsuranceTable = ({ suffix = "", id, loanId }) => {
  const actionsButtons = [
    {
      action: "add",
      path: `${INSURANCE_BASE_PATH}/${INSURANCE_PATHS.ADD_PROPERTY}?${InsuranceParams.PROPERTY}=${id}`,
    },
    {
      action: "edit",
      path: `${INSURANCE_BASE_PATH}/${INSURANCE_PATHS.EDIT_PROPERTY}`,
    },
    "remove",
    "export",
  ];

  return (
    <LmsTable
      actionButtons={actionsButtons}
      apiSuffix={suffix || `loanproperty/${id}/insurances`}
      deleteSuffix="loanproperty/insurances"
      onClickPath={`${INSURANCE_BASE_PATH}/${INSURANCE_PATHS.EDIT_PROPERTY}/{id}?loanId=${loanId}`}
      columnLabelsDictionary={{
        insuranceBrokerOrganization: "Provider",
        insuranceBrokerOrganizationContact: "Provider Agent",
        policyCoverage: "Coverage type",
        policyNumber: "Policy #",
        expiryDate: "Expiration date",
      }}
      rowsBackgroundCustomColor={(rowData) => rowData?.expiryDateColorId}
    />
  );
};

const EditPropertyForm = ({ id, isCopy = false, loanId = "" }) => {
  const loanPropertySuffix = `loanproperty/${id}`;
  const [{ data, isLoading, error }] = useFetchData(loanPropertySuffix);
  const initialData = isCopy ? removeProp(data, "id") : data;
  const propertyLoanId = loanId || data?.loanId || "";
  const myForm = addPropertyFields(new BCFormController(initialData, {
    values: { loanId: propertyLoanId },
    metaData: { isEdit: true },
    submitSuffix: "loanproperty",
    submitMethod: isCopy ? "POST" : "PUT",
    saveAndContinueSuffix: "loanapplications/editproperty",
    deleteSuffix: "loanproperty",
    clearSuffixesOnSubmit: [loanPropertySuffix]
  }), id);

  const uploadBudgetButtonOptions = {
    text: "Upload Budget",
    icon: GlobalConstants.Icons.CARET_UP,
    modalContent: (setIsModalOpen) =>  <UploadBudgetForm loanPropertyData={data} setIsModalOpen={setIsModalOpen} isDisplayedInModal />
  };

  const formActions = isCopy ? ["save-new", "save-continue", "save-close", "close"] : ["save", "save-close", uploadBudgetButtonOptions, "delete", "close"];

  return <>
    <LmsFormGenerator myForm={myForm} isLoading={isLoading} error={error} actions={formActions} onFormChanged={handleOnPropertyFormChange} />
    {!isCopy && propertyLoanId && <LmsTabPages tabPages={{
      Insurance: <PropertyInsuranceTable suffix={`loanproperty/${id}/insurances`} id={id} loanId={propertyLoanId} />,
      "Property Unit": <PropertyUnitTable suffix={`loanproperty/${id}/units`} id={id} />,
      "Draw Summary": <PropertyDrawSummaryTable suffix={`loanproperty/${id}/draws`} id={id} />
    }} />}
  </>;
};

export const propertyUnitTableColumnsDictionary = {
  loanNumber: "Loan #",
  unitNumber: "Unit #",
  applicationNumber: "Application #",
  appraisedValue: "Underwritten Value",
  estimatedSalesPrice: "As Repaired Value",
  unitReconveyanceReference: "Reconveyance Number",
  unitReconveyanceComments: "Reconveyance Comments",
  unitReconveyanceDate: "Reconveyance Date",
  borrowerOrganization: "Borrower",
  numberOfBathroom: "Number Of Bathrooms",
  numberOfBedroom: "Number Of Bedrooms",
  brms: "BRMs",
  initialCos: "Initial COs",
  reviewCos: "Review COs",
  timeFrameToSellOrRefinanceInMonths: "Sell Refi Timeframe",
  buildTimeInMonths: "Work Duration",
  partialDeedReleased: "Paid Off"
};

const PropertyUnitTable = ({ id, suffix = "" }) => {
  const defaultQueryParams = `loanPropertyId=${id}`;
  const editUnitEndpoint = `/servicing-units/edit/{id}?${defaultQueryParams}`;
  const addUnitEndpoint = `/servicing-units/add?${defaultQueryParams}`;
  const actionsButtons = [
    { action: "Add", path: addUnitEndpoint },
    { action: "Copy", text: "Copy", path: `${addUnitEndpoint}&copyUnitId={id}` },
    { action: "Edit", path: editUnitEndpoint },
    "remove",
    "export",
    "save",
    "cancel"
  ];

  return <LmsTable
    actionButtons={actionsButtons}
    apiSuffix={suffix || `loanproperty/${id}/units`}
    onClickPath={editUnitEndpoint}
    deleteSuffix="loanproperty/units"
    calculatedColumns={["asIsValue", "appraisedValue", "estimatedSalesPrice", "partialDeedReleaseAmount", "allocatedCosts", "actualSalesPrice"]}
    columnLabelsDictionary={propertyUnitTableColumnsDictionary}
    saveSuffix="loanproperty/units"
    myForm={loanPropertyUnitReconveyanceFields(new BCFormController())}
  />;
};

const PropertyDrawSummaryTable = ({ id, suffix = "" }) => {
  return <LmsTable
    actionButtons={["export"]}
    apiSuffix={suffix || `loanproperty/${id}/draws`}
    sortable={["Created On"]}
  />;
};

const LmsLoanApplicationTabPages = ({ id = "" }) => {
  const tabPages = {
    Detail: <LmsLoanDetailsTable suffix={`voting/loan-review/list-loan-details/${id}`} />,
    Properties: <LmsLoanPropertyTable suffix={`loans/properties/${id}`} loanId={id} />,
    Entities: <LmsLoanApplicationsEntitiesTable loanId={id} />,
    Comments: <LmsLoanCommentsTable loanId={id} />,
    Condition: <LmsLoanConditionsTable suffix={`loans/conditions/${id}`} loanId={id} source="Loan" overrideDefaultTableProps={{ useApiFiltering: false }} />
  };

  return <LmsTabPages tabPages={tabPages}/>;
};

const LMSLoanApplicationForms = ({ ...props }: IDefaultPagesProps) => {
  const { params = {}, history } = props;
  const allowedActions = ["copy", "add"];
  const propertySubpages = ["addproperty", "copyproperty", "editproperty"];
  const tempPropertySubpages = ["loantempproperty", "addloantempproperty", "editloantempproperty"];
  const allowedSubpages = ["createminiloan","addminiloan", ...propertySubpages, ...tempPropertySubpages] as any[];
  const { isAdd, isEdit, isView, isMainPage, isActionAllowed, actionLower, isSubpageAllowed, id, query } = useParseRouteActions(params, allowedActions, allowedSubpages);
  const loanId = query.get("loanId") || "";

  if (!isActionAllowed) {
    history.goBack();
  }

  return <>
    {isMainPage && <LoanApplicationsTable />}
    {isAdd && <AddApplicationForm />}
    {isEdit && <>
      <EditApplicationForm id={id} />
      <LmsLoanApplicationTabPages id={id} />
    </>}
    {isView && <>
      <ViewApplicationForm id={id} />
      <LmsLoanApplicationTabPages id={id} />
    </>}
    {isSubpageAllowed && <>
      {actionLower === "addminiloan" && <AddMiniLoanApplicationForm minLoanTempId={id} />}
      {actionLower === "loantempproperty" && <LoanTempPropertyUnitTable id={id} />}
      {actionLower === "addloantempproperty" && <AddPropertyTempForm />}
      {actionLower === "editloantempproperty" && <EditPropertyTempForm id={id} />}
      {actionLower === "addproperty" && <AddPropertyForm loanId={loanId} />}
      {(actionLower === "editproperty" || actionLower === "copyproperty") && <EditPropertyForm id={id} isCopy={actionLower === "copyproperty"} loanId={loanId} />}
    </>}
  </>;
};

export default LMSLoanApplicationForms;